import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { usePatientStats } from '../../hooks/usePatientStats';
import LoadingSpinner from '../common/LoadingSpinner';
import StatsCard from '../common/StatsCard';
import { formatNumber } from '../../utils/formatters';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function PatientStatsTab({ dateRange }: Props) {
  const { data: stats, isLoading, error } = usePatientStats(dateRange);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Erreur lors du chargement des données</div>;
  if (!stats) return null;

  // Préparer les données pour les graphiques
  const monthlyData = stats.patients_by_month.map(item => ({
    month: new Date(item.month).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
    patients: item.count
  }));

  const insuranceData = [
    { name: 'Avec assurance', value: stats.insurance_coverage.with_insurance },
    { name: 'Sans assurance', value: stats.insurance_coverage.without_insurance }
  ];

  return (
    <div className="space-y-6">
      {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Patients"
          value={formatNumber(stats.total_patients)}
          icon="👥"
          color="blue"
        />
        <StatsCard
          title="Avec Assurance"
          value={formatNumber(stats.insurance_coverage.with_insurance)}
          icon="🛡️"
          color="green"
        />
        <StatsCard
          title="Sans Assurance"
          value={formatNumber(stats.insurance_coverage.without_insurance)}
          icon="⚠️"
          color="red"
        />
        <StatsCard
          title="Taux d'Assurance"
          value={`${stats.total_patients > 0 ? ((stats.insurance_coverage.with_insurance / stats.total_patients) * 100).toFixed(1) : 0}%`}
          icon="📊"
          color="purple"
        />
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Évolution mensuelle */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Évolution Mensuelle des Patients</h3>
          <LineChart width={500} height={300} data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="patients" stroke="#8884d8" strokeWidth={2} />
          </LineChart>
        </div>

        {/* Répartition assurance */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Répartition par Assurance</h3>
          <PieChart width={500} height={300}>
            <Pie
              data={insuranceData}
              cx={250}
              cy={150}
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {insuranceData.map((entry, index: number) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </div>

        {/* Top assurances */}
        <div className="bg-white p-6 rounded-lg shadow lg:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Compagnies d'Assurance</h3>
          <BarChart width={1000} height={300} data={stats.insurance_stats.slice(0, 10)}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="assureur__nom" 
              angle={-45} 
              textAnchor="end" 
              height={100}
              interval={0}
            />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#8884d8" />
          </BarChart>
        </div>
      </div>
    </div>
  );
}